import React from "react"
import autobind from "class-autobind"
import { Dialog, FlatButton, TextField, Toggle } from "material-ui"
import strings from "../../localization/strings"
import { IUserDocument, ISchedulePreference, ISimpleTime } from "../../models"
import * as utils from "../../helpers/utils"
import SimpleTimeEditor from "../../components/common/SimpleTimeEditor"
import moment from "moment"
import IconDatePicker from "../common/IconDatePicker"
import * as timeHelper from "../../helpers/timeHelper"
import { orange700 } from "material-ui/styles/colors"

interface IProps {
    employee: IUserDocument
    existingSchedulePreference: ISchedulePreference[]
    editSchedulePreference: ISchedulePreference
    handleClose(): void
    handleAdd(schedulePreference: ISchedulePreference): void
    handleUpdate(schedulePreference: ISchedulePreference): void
}
interface IState {
    preferred: boolean
    unAvailable: boolean
    workDay: string //it's browser local time
    errorWorkDay?: string
    startTime: ISimpleTime
    endTime: ISimpleTime
    note: string
    errorSaving?: string
}

export default class AddUserDetailSchedulePreferenceDialog extends React.Component<IProps, IState> {
    constructor(props: IProps) {
        super(props)

        this.state = {
            preferred: props.editSchedulePreference ? props.editSchedulePreference.Preferred : true,
            unAvailable: props.editSchedulePreference ? props.editSchedulePreference.UnAvailable : false,
            workDay: moment().add(1, "days").format(),
            startTime: { Hour: 9, Minute: 0 },
            endTime: { Hour: 17, Minute: 0 },
            errorWorkDay: "",
            note: props.editSchedulePreference ? props.editSchedulePreference.Note : ""
        }
        autobind(this)
    }

    async componentDidMount() {
        if (this.props.editSchedulePreference) {
            const startTime = await timeHelper.getDateTime(this.props.employee.DefaultPunchHierarchyId, this.props.employee.DefaultJobId, this.props.editSchedulePreference.StartTime)
            const endTime = await timeHelper.getDateTime(this.props.employee.DefaultPunchHierarchyId, this.props.employee.DefaultJobId, this.props.editSchedulePreference.EndTime)
            timeHelper.convertDefaultDateToNull(this.props.editSchedulePreference.StartTime) &&
                this.setState({ workDay: timeHelper.keepConvertUTCToLocal(moment(startTime).utc(true).format()) })
            this.props.editSchedulePreference.StartTime &&
                this.setState({ startTime: { Hour: startTime.get("hours"), Minute: startTime.get("minutes") } })
            this.setState({ endTime: { Hour: endTime.get("hours"), Minute: endTime.get("minutes") } })
        }
    }

    async handleSave() {
        const validate = await this.checkValidate()
        if (!validate) return

        const startTime = await this.getUTCTime(this.state.workDay, this.state.startTime)
        let endTime = await this.getUTCTime(this.state.workDay, this.state.endTime)
        if (moment(startTime).isSameOrAfter(endTime))
            endTime = moment(endTime).add("day", 1).format()

        if (this.props.editSchedulePreference) {
            const payload = this.props.editSchedulePreference
            payload.Preferred = this.state.preferred
            payload.UnAvailable = this.state.unAvailable
            payload.StartTime = startTime
            payload.EndTime = endTime
            payload.Note = this.state.note
            this.props.handleUpdate(payload)
        }
        else {
            const payload = {
                Id: utils.guid(),
                Preferred: this.state.preferred,
                UnAvailable: this.state.unAvailable,
                StartTime: startTime,
                EndTime: endTime,
                Note: this.state.note,

            } as ISchedulePreference
            this.props.handleAdd(payload)
        }

        this.props.handleClose()
    }

    async getUTCTime(dateString: string, simpleTime: ISimpleTime) {
        const timezoneId = await timeHelper.getIANATimezoneNameFromHierarchyJobId(this.props.employee.DefaultPunchHierarchyId, this.props.employee.DefaultJobId)
        return timeHelper.getTimezoneTranslatedDateTime(dateString, simpleTime.Minute, simpleTime.Hour, timezoneId)
    }

    async checkValidate() {
        let isValidate = true

        const startTime = await this.getUTCTime(this.state.workDay, this.state.startTime)
        if (moment(startTime).isBefore(moment().endOf("date").utc())) {
            this.setState({ errorSaving: strings.Work_Preference_is_not_allowed_for_past_and_current_date })
            isValidate = false
        }
        else {
            this.setState({ errorSaving: "" })
        }

        const workDayClientLocalTime = await timeHelper.getDateTime(this.props.employee.DefaultPunchHierarchyId, this.props.employee.DefaultJobId, moment(this.state.workDay).utc(true))

        const existingPreferenceDayList = await Promise.all(
            this.props.existingSchedulePreference.map(async i => {
                if (this.props.editSchedulePreference && this.props.editSchedulePreference.Id === i.Id) {
                    return null
                }
                else {
                    const clientLocalTime = await timeHelper.getDateTime(this.props.employee.DefaultPunchHierarchyId, this.props.employee.DefaultJobId, i.StartTime)
                    if (clientLocalTime.startOf("date").isSame(workDayClientLocalTime.startOf("date"))) {
                        return i
                    }
                    else {
                        return null
                    }
                }
            })
        )

        if (existingPreferenceDayList.find(i => i !== null)) {
            this.setState({ errorWorkDay: strings.Cannot_make_for_existing_date })
            isValidate = false
        }
        else {
            this.setState({ errorWorkDay: "" })
        }

        return isValidate
    }

    render() {
        return (
            <Dialog
                open
                autoScrollBodyContent
                title={this.props.editSchedulePreference ? strings.Edit_Work_Preference : strings.New_Work_Preference}
                titleStyle={{ paddingLeft: 35, paddingBottom: 10 }}
                actions={[
                    <span style={{ fontSize: 19, marginRight: 75, color: orange700 }}>
                        {this.state.errorSaving}
                    </span>,
                    <FlatButton
                        data-intercom-target={"menu-Employees-WorkPref-Btn-Close-AddWorkPrefDialog"}
                        label={strings.CANCEL}
                        secondary
                        onClick={this.props.handleClose}
                    />,
                    <FlatButton
                        data-intercom-target={"menu-Employees-WorkPref-Btn-SaveWorkPref"}
                        label={strings.SAVE}
                        primary
                        onClick={this.handleSave}
                    />
                ]}
            >
                <hr />
                <div style={{ paddingRight: 20, paddingLeft: 20 }}>
                    <div style={{ display: "flex", marginTop: 25 }}>
                        <div style={{ flex: 1 }}>
                            <Toggle
                                data-intercom-target={"menu-Employees-WorkPref-Toggle-Preferred"}
                                label={strings.Preferred}
                                style={{ width: 225 }}
                                toggled={this.state.preferred}
                                onToggle={(e, value) => this.setState({ preferred: value, unAvailable: !value })}
                            />
                        </div>
                        <div style={{ flex: 1 }}>
                            <Toggle
                                data-intercom-target={"menu-Employees-WorkPref-Toggle-Unavailable"}
                                label={strings.Unavailable}
                                style={{ width: 225 }}
                                toggled={this.state.unAvailable}
                                onToggle={(e, value) => this.setState({ unAvailable: value, preferred: !value })}
                            />
                        </div>
                    </div>
                    <div style={{ display: "flex", marginTop: 15 }}>
                        <div style={{ flex: 1 }}>
                            <IconDatePicker
                                label={strings.Work_Day}
                                errorText={this.state.errorWorkDay}
                                date={this.state.workDay}
                                minDate={!this.props.editSchedulePreference ? moment().add("day", 1).format() : null}
                                handleSelectDate={workDay => this.setState({ workDay: workDay as string }, () => this.checkValidate())}
                            />
                        </div>
                        <div style={{ flex: 1, display: "flex", height: 75 }}>
                            <div>
                                <SimpleTimeEditor
                                    label={strings.Start_Time}
                                    simpleTime={this.state.startTime}
                                    handleChange={(startTime) => this.setState({ startTime }, () => this.checkValidate())}
                                />
                            </div>,
                            <div style={{ marginLeft: 25 }}>
                                <SimpleTimeEditor
                                    label={strings.End_Time}
                                    simpleTime={this.state.endTime}
                                    handleChange={(endTime) => this.setState({ endTime }, () => this.checkValidate())}
                                />
                            </div>
                        </div>
                    </div>
                    <TextField
                        data-intercom-target={"menu-Employees-WorkPref-Entry-Note"}
                        multiLine
                        rows={3}
                        rowsMax={4}
                        floatingLabelText={strings.Note}
                        value={this.state.note}
                        onChange={event => this.setState({ note: event.target.value })}
                        fullWidth
                    />
                </div>
            </Dialog>
        )
    }
}